/**
 * What the card processor actually charges this platform (GH-839, made a setting by GH-844).
 * Somebody else's price list, not a rule of ours.
 *
 * **Nothing in the money path may depend on this.** The processor's real fee is booked from the
 * settlement it reports (`settlement.ts`); this model answers three questions only:
 *
 *  1. the admin console's preview — what the platform keeps on each gift after the processor;
 *  2. the "this schedule loses money" warning, and the confirmation a losing save needs;
 *  3. the daily close's informational check that the model still matches what the processor
 *     actually charged (`processorCostCheck`), so a repricing — Stripe's nonprofit rate taking
 *     effect, a negotiated rate — is visible on the day it starts.
 *
 * Until GH-844 the price was a literal (2.9% + 30¢). It is now `platform_settings.processor_cost`,
 * editable by platform staff with a step-up and a reason, because the owner has applied for
 * Stripe's nonprofit price (2.2% + 30¢) and will negotiate lower as volume grows; a literal would
 * keep reporting the old price after the new one took effect. The fee schedule is a separate
 * setting and is not tied to this one: the fee is set at the market rate, and whatever the
 * processor charges below it is the platform's margin.
 *
 * Rounding: the processor's percentage is modelled **half-up** to the cent — the one rule the
 * setting admits today (`roundingRule: "half_up"`). The platform fee rounds its percentage *up*,
 * which is ≥ any round-to-nearest, so at an equal rate and fixed part the fee is never below cost.
 *
 * Bank debits (Stripe ACH Direct Debit) are a second entry on the same setting: 0.8% of the debit,
 * capped at $5.00, no fixed part. The fee schedule does not vary by method (a fee that did would be
 * a card surcharge), so the ACH line is informational — what the platform keeps when a donor pays
 * by bank. The loss warning is computed against the card price, the dearer rail at every amount a
 * sane setting describes.
 */
import { money, zero } from "./money.js";
export const PROCESSOR_COST_ROUNDING_RULES = ["half_up"];
/**
 * Stripe's standard US prices: cards and wallets 2.9% + 30¢; ACH Direct Debit 0.8% capped at $5.
 * Operator-editable — `platform_settings.processor_cost` overrides it.
 */
export const DEFAULT_PROCESSOR_COST = Object.freeze({
    rateBps: 290n,
    fixedMinor: 30n,
    roundingRule: "half_up",
    achRateBps: 80n,
    achCapMinor: 500n,
});
/** Guard rails on what an operator may record. Wide on purpose: this is a fact, not a choice. */
export const MAX_PROCESSOR_COST_BPS = 1000n; // 10%
export const MAX_PROCESSOR_COST_FIXED_MINOR = 100n; // $1.00
export const MAX_ACH_PROCESSOR_COST_BPS = 1000n; // 10%
export const MAX_ACH_PROCESSOR_COST_CAP_MINOR = 2000n; // $20.00
/** Kept for the GH-839 tests and any caller that wants Stripe's standard price by name. */
export const MODELLED_CARD_RATE_BPS = DEFAULT_PROCESSOR_COST.rateBps;
export const MODELLED_CARD_FIXED_MINOR = DEFAULT_PROCESSOR_COST.fixedMinor;
const BPS = 10000n;
const HALF = 5000n;
export function isValidProcessorCost(cost) {
    return (PROCESSOR_COST_ROUNDING_RULES.includes(cost.roundingRule) &&
        cost.rateBps >= 0n &&
        cost.rateBps <= MAX_PROCESSOR_COST_BPS &&
        cost.fixedMinor >= 0n &&
        cost.fixedMinor <= MAX_PROCESSOR_COST_FIXED_MINOR &&
        cost.achRateBps >= 0n &&
        cost.achRateBps <= MAX_ACH_PROCESSOR_COST_BPS &&
        cost.achCapMinor >= 0n &&
        cost.achCapMinor <= MAX_ACH_PROCESSOR_COST_CAP_MINOR);
}
/** `round-half-up(rateBps · x / 10 000)` for a non-negative x. */
const percentHalfUp = (amountMinor, rateBps) => (amountMinor * rateBps + HALF) / BPS;
/** What the processor charges on one card or wallet charge of `charged`. Zero for no charge. */
export function cardProcessingCost(charged, cost = DEFAULT_PROCESSOR_COST) {
    if (charged.amountMinor <= 0n)
        return zero(charged.currency);
    return money(percentHalfUp(charged.amountMinor, cost.rateBps) + cost.fixedMinor, charged.currency);
}
/** What the processor charges on one bank debit of `charged`: the percentage, capped. */
export function achProcessingCost(charged, cost = DEFAULT_PROCESSOR_COST) {
    if (charged.amountMinor <= 0n)
        return zero(charged.currency);
    const percentage = percentHalfUp(charged.amountMinor, cost.achRateBps);
    return money(percentage > cost.achCapMinor ? cost.achCapMinor : percentage, charged.currency);
}
/** The processing cost for a charge made with a given method class. */
export function processingCostFor(methodClass, charged, cost = DEFAULT_PROCESSOR_COST) {
    return methodClass === "ach"
        ? achProcessingCost(charged, cost)
        : cardProcessingCost(charged, cost);
}
/** Stripe's standard card price (2.9% half-up + 30¢). The GH-839 name, kept for its callers. */
export function modelledCardProcessingFee(charged) {
    return cardProcessingCost(charged, DEFAULT_PROCESSOR_COST);
}
/* ----------------------------------------------- in person (Tap to Pay on iPhone, GH-848) */
/**
 * Stripe's standard US price for a card taken **in person** with Tap to Pay on iPhone, as
 * published on stripe.com/pricing (read 2026-10-02): **2.7% + 5¢** per successful domestic card
 * payment, **plus $0.10 per authorization** for Tap to Pay — modelled here as 2.7% + 15¢.
 * International cards add 1.5%, which this model, like the online one, leaves out.
 *
 * Informational only, exactly like `DEFAULT_PROCESSOR_COST`: **nothing in the money path reads
 * it.** Donors in person see the same fee schedule as online (DEC-047's default, and the only
 * choice that cannot be a card surcharge — `platform-fee.ts`), so on an in-person gift the
 * platform's margin is wider than online by `cardProcessingCost(T) − tapToPayProcessingCost(T)`:
 * 0.2% of the charge plus 15¢. A declined authorization still costs its $0.10, which a gift that
 * never completes has no fee to pay for — the daily close books what Stripe actually reports.
 */
export const STRIPE_TAP_TO_PAY_US_PRICE = Object.freeze({
    rateBps: 270n,
    fixedMinor: 15n,
    roundingRule: "half_up",
    // No bank debit in person; carried so the shape is the one the card functions take.
    achRateBps: DEFAULT_PROCESSOR_COST.achRateBps,
    achCapMinor: DEFAULT_PROCESSOR_COST.achCapMinor,
});
/** What Stripe charges on one in-person Tap to Pay charge of `charged` (2.7% half-up + 15¢). */
export function tapToPayProcessingCost(charged, price = STRIPE_TAP_TO_PAY_US_PRICE) {
    return cardProcessingCost(charged, price);
}
/* ------------------------------------------------------------------- words */
const formatRate = (bps) => {
    const fraction = (bps % 100n).toString(10).padStart(2, "0").replace(/0+$/, "");
    return fraction ? `${bps / 100n}.${fraction}%` : `${bps / 100n}%`;
};
const formatDollars = (minor) => `$${(minor / 100n).toString(10)}.${(minor % 100n).toString(10).padStart(2, "0")}`;
/** "2.2% + $0.30" — the card price in words. */
export function formatCardProcessorCost(cost) {
    return cost.fixedMinor > 0n
        ? `${formatRate(cost.rateBps)} + ${formatDollars(cost.fixedMinor)}`
        : formatRate(cost.rateBps);
}
/** "0.8%, capped at $5.00" — the bank-debit price in words. */
export function formatAchProcessorCost(cost) {
    return `${formatRate(cost.achRateBps)}, capped at ${formatDollars(cost.achCapMinor)}`;
}
const ceilDiv = (numerator, denominator) => (numerator + denominator - 1n) / denominator;
/**
 * The smallest gift on which a fee schedule leaves the platform with less than the processor's
 * card price — or null when there is none, at any size. **Exact, not sampled.**
 *
 * With `fee(y) = ⌈r·y/D⌉ + f` and `cost(y) = ⌊(c·y + D/2)/D⌋ + g` on the charged amount `y`, the
 * platform's net `n(y) = fee(y) − cost(y)` satisfies `n(y + D) = n(y) + (r − c)` exactly (both
 * percentages advance by a whole `r` and `c` cents over `D` cents). So one period of `D = 10 000`
 * charged amounts decides everything:
 *
 *  - **Declined** (charged = gift, gifts ≥ $1): for each residue `y` in `[min, min + D)`, the first
 *    losing member of `y, y + D, y + 2D, …` is at `k = 0` when `n(y) < 0`, never when `r ≥ c`, and
 *    otherwise at `k = ⌊n(y)/(c − r)⌋ + 1`. The answer is the least `y + kD`.
 *  - **Covered** (charged = the gross-up `T`, `T − fee(T) = gift`): `T` is some gift's gross-up
 *    exactly when `fee(T) = fee(T − 1)`, a condition with period `D`; and the gift for `T + kD` is
 *    `gift(T) + k(D − r)`. The same residue argument over `[T(min), T(min) + D)` gives the least
 *    losing gift, because the gift rises with `T`.
 *
 * 2 × 10 000 bigint evaluations, so it runs on every preview. The unit tests cross-check it against
 * a brute-force sweep of every cent from $1 to $2,000 over a grid of schedules and prices.
 */
export function firstLosingGift(schedule, cost, minimumGiftMinor = 100n) {
    const r = schedule.rateBps;
    const f = schedule.fixedMinor;
    const c = cost.rateBps;
    const g = cost.fixedMinor;
    const fee = (y) => (y <= 0n ? 0n : ceilDiv(r * y, BPS) + f);
    const net = (y) => fee(y) - (percentHalfUp(y, c) + g);
    const stepsUntilLoss = (y) => {
        const now = net(y);
        if (now < 0n)
            return 0n;
        if (r >= c)
            return null;
        return now / (c - r) + 1n;
    };
    let declined = null;
    for (let y = minimumGiftMinor; y < minimumGiftMinor + BPS; y += 1n) {
        const k = stepsUntilLoss(y);
        if (k === null)
            continue;
        const gift = y + k * BPS;
        if (declined === null || gift < declined)
            declined = gift;
    }
    let covered = null;
    const firstTotal = ceilDiv(BPS * (minimumGiftMinor + f), BPS - r);
    for (let t = firstTotal; t < firstTotal + BPS; t += 1n) {
        // Only totals some gift grosses up to: the smallest T for its gift.
        if (t !== firstTotal && fee(t) !== fee(t - 1n))
            continue;
        const k = stepsUntilLoss(t);
        if (k === null)
            continue;
        const gift = t - fee(t) + k * (BPS - r);
        if (covered === null || gift < covered)
            covered = gift;
    }
    if (declined === null && covered === null)
        return null;
    if (covered === null || (declined !== null && declined <= covered)) {
        return { giftMinor: declined, choice: "declined" };
    }
    return { giftMinor: covered, choice: "covered" };
}
//# sourceMappingURL=processor-cost-model.js.map